import React from "react";
import cn from "classnames";
import {useSceneScope} from "hooks/useSceneScope";
import {DIMENSIONS} from "libs/sceneScope";
import "./ScopeBar.css";

/**
 * Who receives the sidebar's edits: every banner, or the banners ticked in
 * their headers. Also the row of background operations that used to exist
 * only per banner — fit, flip, rotate — applied to the same scope. Renders
 * nothing off a scopes channel (libs/sceneScope.ts).
 *
 * It also renders nothing until at least one banner is ticked. With nothing
 * selected there is no scope to choose — `Selection` is disabled and every
 * edit goes to the shared value anyway — so the bar was two rows of controls
 * answering a question the user had not asked. A banner's own background tools
 * tick it (layout/BgTransformer), so the bar appears the moment a banner is
 * engaged and leaves again when the selection is released. That also means the
 * background operations here are selection-only: with nothing ticked, each
 * banner's own tool panel is the way to fit or flip it.
 */
const ScopeBar = () => {
  const scope = useSceneScope();
  if (!scope.scoped) return null;

  const count = scope.selectedTemplates.length;
  if (count === 0) return null;
  const isSelection = scope.isSelection;
  // The settings context is untyped JS; its setters take one value.
  const setScopeMode = scope.editScopeMode as unknown as (mode: "all" | "selection") => void;

  return (
    <div className="scope-bar" data-scope={isSelection ? "selection" : "all"}>
      <div className="scope-bar__row">
        <span className="scope-bar__label">Edits go to</span>
        <div className="scope-bar__modes" role="radiogroup" aria-label="Scope">
          <button
            type="button"
            role="radio"
            aria-checked={!isSelection}
            className={cn("scope-bar__mode", {selected: !isSelection})}
            onClick={() => setScopeMode("all")}
            title="Shared values: every banner that is not set apart follows them"
          >
            All banners
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={isSelection}
            className={cn("scope-bar__mode", {selected: isSelection})}
            disabled={count === 0}
            onClick={() => setScopeMode("selection")}
            title={
              count
                ? "Only the ticked banners: each gets a value of its own"
                : "Tick banners in their headers to edit a selection"
            }
          >
            Selection{count ? ` (${count})` : ""}
          </button>
          {count > 0 && (
            <button
              type="button"
              className="scope-bar__link"
              onClick={scope.clearSelection}
              title="Untick every banner"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {/* Positioning, for the banners in scope that have it: a ready cut
          (BgTransformer/templateBg.ts) shows as it is. */}
      {scope.bgOperationTargets.length > 0 && (
        <div className="scope-bar__row">
          <span className="scope-bar__label">Background</span>
          <div className="scope-bar__ops">
            <button type="button" className="scope-bar__op" onClick={() => scope.applyBgOperation("fit")} title="Cover-fit the background of every banner in scope">
              Fit
            </button>
            <button type="button" className="scope-bar__op" onClick={() => scope.applyBgOperation("reset")} title="Put the background of every banner in scope back to its default position">
              Reset
            </button>
            <button type="button" className="scope-bar__op" onClick={() => scope.applyBgOperation("flipH")} title="Flip horizontally in scope">
              Flip H
            </button>
            <button type="button" className="scope-bar__op" onClick={() => scope.applyBgOperation("flipV")} title="Flip vertically in scope">
              Flip V
            </button>
            <button type="button" className="scope-bar__op" onClick={() => scope.applyBgOperation("rotate", 90)} title="Rotate by 90° in scope">
              90°
            </button>
          </div>
        </div>
      )}
      {isSelection && (
        <div className="scope-bar__row">
          <span className="scope-bar__label">Reset selection</span>
          <div className="scope-bar__ops">
            {DIMENSIONS.map((d) => (
              <button
                key={d.id}
                type="button"
                className="scope-bar__op"
                title={`Return the selected banners' ${d.label.toLowerCase()} to the default, or to the shared value`}
                onClick={() => scope.selection?.forEach((id) => scope.resetTemplate(id, d.id, "default"))}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopeBar;
